import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { designSystem } from '@/styles/theme';
import { useBackToClose } from '@/common/useBackToClose';
import { FILTER_MOBILE_MAX_WIDTH_PX, filterTokens } from './filterTokens';

export interface SortOption<T extends string> {
    name: string;
    value: T;
}

/**
 * 폰 목록의 조작 줄 — 필터 머리 바로 아래, 본문 맨 위 한 줄(2026-10-01 사용자 시안 확인).
 * 왼쪽에 바로 켜고 끄는 조건([CheckToggle]: 급구만 보기·무료만 보기), 오른쪽에 정렬([SortSelect]).
 * 네 목록(채용·세미나·전문가 라운지·속닥속닥)이 같은 줄을 쓴다. PC 는 이 줄을 그리지 않고 [SortSelect] 만 필터 줄 오른쪽 끝에 둔다.
 */
export const ListControlRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    /* 폰 전용 줄 — 칩(폰 32px)과 같은 높이. 좌우는 카드 끝에서 4px 안쪽 */
    min-height: 32px;
    /* 위아래 여백을 똑같이 2px — 아래 내용이 스스로 가진 위 여백([listControlGap] 으로 넘긴 값)만큼 당겨서 맞춘다 */
    margin: 2px 4px calc(2px - var(--list-control-content-gap, 0px));
    /* 당긴 만큼 아래 내용의 빈 위 여백이 이 줄과 겹친다 — 버튼이 그 밑에 깔려 안 눌리지 않게 위로 올린다 */
    position: relative;
    z-index: 1;
`;

/** 조작 줄 바로 아래 내용이 스스로 가진 위 여백(px) — 줄 아래 여백을 위 여백과 같게 맞추는 데 쓴다 */
export const listControlGap = (contentTopGapPx: number): React.CSSProperties =>
    ({ ['--list-control-content-gap' as string]: `${contentTopGapPx}px` });

const ToggleButton = styled.button`
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 36px;
    /* 아이콘 안쪽 여백(2px)만큼 당겨 동그라미가 줄의 왼쪽 기준선에 맞는다 */
    margin-left: -2px;
    padding: 0 8px 0 0;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: ${filterTokens.color.textSub};
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;

    & > svg { font-size: 20px; }
    &[aria-checked='true'] {
        color: var(--toggle-on);
        font-weight: 700;
    }
    /* 폰: 칩과 같은 크기(높이 32, 글자 13). 동그라미는 글자와 어울리게 16px */
    @media (max-width: ${FILTER_MOBILE_MAX_WIDTH_PX}px) {
        height: 32px;
        font-size: 13px;
        & > svg { font-size: 16px; }
    }
    &:focus-visible {
        outline: 2px solid ${filterTokens.color.accent};
        outline-offset: 2px;
        border-radius: 8px;
    }
`;

export interface CheckToggleProps {
    label: string;
    checked: boolean;
    /** 켰을 때 색 — 기본은 필터에서 고른 글자색. 급구처럼 뜻이 있는 색만 따로 넘긴다 */
    onColor?: string;
    onChange: (next: boolean) => void;
}

/** 누르면 바로 켜고 끄는 조건 — 동그란 체크 + 글자. 칩 줄의 토글 칩과 같은 동작이다 */
export const CheckToggle = ({ label, checked, onColor = filterTokens.color.selectedText, onChange }: CheckToggleProps) => (
    <ToggleButton
        type="button"
        role="switch"
        aria-checked={checked}
        style={{ ['--toggle-on' as string]: onColor }}
        onClick={() => onChange(!checked)}
    >
        {checked ? <CheckCircleRoundedIcon aria-hidden="true" /> : <RadioButtonUncheckedRoundedIcon aria-hidden="true" />}
        {label}
    </ToggleButton>
);

const SortWrap = styled.div`
    position: relative;
    margin-left: auto;
`;

const SortTrigger = styled.button`
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 36px;
    /* 오른쪽 끝(펼쳤을 때 깔리는 바탕 포함)이 본문 기준선에 맞는다 */
    padding: 0 2px 0 10px;
    border: none;
    border-radius: 8px;
    background: none;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: ${filterTokens.color.text};
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;

    & > svg {
        font-size: 20px;
        transition: transform 0.15s ease;
    }
    &[data-changed='true'] {
        color: ${filterTokens.color.selectedText};
        font-weight: 700;
    }
    &[aria-expanded='true'] {
        background: ${filterTokens.color.selectedBg};
    }
    &[aria-expanded='true'] > svg { transform: rotate(180deg); }
    &:focus-visible {
        outline: 2px solid ${filterTokens.color.accent};
        outline-offset: 2px;
    }
    /* 폰: 칩과 같은 크기(높이 32, 글자 13). 줄 위아래 여백이 2px 뿐이라, 펼쳤을 때 깔리는 바탕은 버튼 전체가 아니라
       위아래 4px 안쪽(높이 24px)에만 칠한다 — 누르는 영역은 32px 그대로 */
    @media (max-width: ${FILTER_MOBILE_MAX_WIDTH_PX}px) {
        position: relative;
        isolation: isolate;
        height: 32px;
        padding: 0 2px 0 8px;
        font-size: 13px;
        & > svg { font-size: 18px; }

        &[aria-expanded='true'] { background: none; }
        &[aria-expanded='true']::before {
            content: '';
            position: absolute;
            inset: 4px 0;
            z-index: -1;
            border-radius: 6px;
            background: ${filterTokens.color.selectedBg};
        }
    }
`;

const SortMenu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: ${designSystem.zIndex.dropdown};
    width: 168px;
    padding: 6px 0;
    border: 1px solid ${filterTokens.color.border};
    border-radius: 12px;
    background: ${filterTokens.color.surface};
    box-shadow: 0 14px 36px rgba(20, 30, 28, 0.18);
`;

const SortItem = styled.button`
    appearance: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    height: 44px;
    padding: 0 14px;
    border: none;
    background: none;
    font-family: inherit;
    font-size: 15px;
    font-weight: 500;
    color: ${filterTokens.color.text};
    text-align: left;
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;

    & > svg { font-size: 20px; }
    &[aria-selected='true'] {
        color: ${filterTokens.color.selectedText};
        font-weight: 700;
    }
    &:active { background: ${filterTokens.color.selectedBg}; }
    &:focus-visible {
        outline: 2px solid ${filterTokens.color.accent};
        outline-offset: -2px;
    }
`;

export interface SortSelectProps<T extends string> {
    options: SortOption<T>[];
    value: T;
    /** 기본 정렬 — 기본이 아닐 때만 버튼을 고른 색으로 그린다 */
    defaultValue: T;
    onChange: (next: T) => void;
}

/**
 * 목록 정렬 — 글자 + 화살표 버튼, 누르면 바로 아래에 목록이 겹쳐 뜨는 드롭다운. 네 목록의 PC·폰이 모두 이 부품 하나를 쓴다
 * (폰: 본문 맨 위 조작 줄 오른쪽 / PC: 필터 줄 오른쪽 끝).
 * 항목을 누르면 바로 적용하고 닫는다. 바깥을 누르거나 화면을 움직이면 닫힌다(화면을 어둡게 덮지 않는다).
 */
export const SortSelect = <T extends string>({ options, value, defaultValue, onChange }: SortSelectProps<T>) => {
    const [open, setOpen] = useState(false);
    // 연 채 뒤로 가기(안드로이드 뒤로 키 등)면 이전 페이지로 나가지 않고 펼침만 닫는다(공용 시트와 같은 방문 기록 칸 방식)
    const leave = useBackToClose(open, () => setOpen(false));
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const name: string = options.find((option: SortOption<T>) => option.value === value)?.name ?? options[0]?.name ?? '';

    useEffect(() => {
        if (!open) return;
        const close = (): void => setOpen(false);
        const onPointerDown = (event: PointerEvent): void => {
            if (!wrapRef.current?.contains(event.target as Node)) close();
        };
        document.addEventListener('pointerdown', onPointerDown);
        // 화면이 창이 아닌 안쪽 영역에서 구르는 경우도 잡는다(scroll 은 위로 전달되지 않아 capture 로 듣는다)
        document.addEventListener('scroll', close, { capture: true, passive: true });
        window.addEventListener('resize', close);
        wrapRef.current?.querySelector<HTMLButtonElement>('[role="option"][aria-selected="true"]')?.focus({ preventScroll: true });
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('scroll', close, { capture: true });
            window.removeEventListener('resize', close);
        };
    }, [open]);

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            triggerRef.current?.focus();
            return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        const items: HTMLButtonElement[] = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="option"]'));
        const current: number = items.indexOf(document.activeElement as HTMLButtonElement);
        const step: number = event.key === 'ArrowDown' ? 1 : -1;
        items[(current + step + items.length) % items.length]?.focus();
    };

    return (
        <SortWrap ref={wrapRef}>
            <SortTrigger
                ref={triggerRef}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`정렬: ${name}`}
                data-changed={value !== defaultValue}
                onClick={() => setOpen((previous: boolean) => !previous)}
            >
                {name}
                <KeyboardArrowDownRoundedIcon aria-hidden="true" />
            </SortTrigger>
            {open && (
                <SortMenu role="listbox" aria-label="정렬" onKeyDown={handleMenuKeyDown}>
                    {options.map((option: SortOption<T>) => (
                        <SortItem
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={option.value === value}
                            onClick={() => {
                                // 쌓은 방문 기록 칸을 먼저 되돌린 뒤 바꾼다 — 정렬은 주소(쿼리)를 바꾸므로 되돌리기가 그 칸을 지우지 않게
                                leave(() => {
                                    if (option.value !== value) onChange(option.value);
                                });
                                setOpen(false);
                            }}
                        >
                            {option.name}
                            {option.value === value && <CheckRoundedIcon aria-hidden="true" />}
                        </SortItem>
                    ))}
                </SortMenu>
            )}
        </SortWrap>
    );
};
